import React from 'react';
import { Box, Typography, Card, CardContent, Grid, useTheme } from '@mui/material';
import { SolarPower, Battery80, ElectricBolt, Power } from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

/**
 * SystemDesignSummary Component
 * Displays the system design and asset sizes from REopt results
 */
export default function SystemDesignSummary({ results }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    if (!results || !results.outputs) {
        return null;
    }

    const outputs = results.outputs || {};
    const inputs = results.inputs || {};

    // Helper to extract system sizing data from outputs
    const getSizing = (component, sizeKey) => {
        // Try multiple possible locations (flat v3 vs nested structure)
        const siteOutputs = outputs.Scenario?.Site || {};
        return outputs[component]?.[sizeKey] ||
            siteOutputs[component]?.[sizeKey] ||
            null;
    };

    // Extract system sizes
    const pvSizeKw = getSizing('PV', 'size_kw');
    const batterySizeKw = getSizing('ElectricStorage', 'size_kw');
    const batterySizeKwh = getSizing('ElectricStorage', 'size_kwh');

    // Extract generator sizes from outputs
    const generatorSizeKw = getSizing('Generator', 'size_kw') || 0;
    const chpSizeKw = getSizing('CHP', 'size_kw') || 0;
    const totalGeneratorSizeKw = generatorSizeKw + chpSizeKw; // Total for hasAssets check

    // Check if off-grid
    const isOffGrid = inputs.Settings?.off_grid_flag ||
        inputs.Scenario?.off_grid_flag ||
        inputs.Scenario?.Site?.off_grid_flag ||
        false;

    // Determine fuel type from inputs - checks multiple sources to detect generator fuel type
    // Priority: 1) Explicit fuel_type field, 2) CHP presence, 3) Fuel cost fields
    const getGeneratorFuelType = () => {
        const generatorInput = inputs.Generator || inputs.Scenario?.Site?.Generator || {};
        const chpInput = inputs.CHP || inputs.Scenario?.Site?.CHP || {};

        // If we have both Generator and CHP with sizes, it's dual-fuel
        if (generatorSizeKw > 0 && chpSizeKw > 0) {
            return 'diesel_and_natural_gas';
        }

        // Check CHP first (CHP is always natural gas)
        if (chpSizeKw > 0 || chpInput.max_kw > 0) {
            return 'natural_gas';
        }

        // Check explicit fuel_type on Generator input (backend now preserves this from CHP conversion)
        const explicitFuelType = generatorInput.fuel_type;
        if (explicitFuelType) {
            const normalized = explicitFuelType.toLowerCase().replace(/\s+/g, '_');
            if (normalized === 'natural_gas' || normalized === 'ng') {
                return 'natural_gas';
            }
            if (normalized === 'propane') {
                return 'propane';
            }
            if (normalized === 'diesel') {
                return 'diesel';
            }
            return normalized; // biogas, etc.
        }

        // Infer from fuel cost fields if no explicit fuel_type
        // fuel_cost_per_mmbtu is specific to natural gas
        if (generatorInput.fuel_cost_per_mmbtu !== undefined || chpInput.fuel_cost_per_mmbtu !== undefined) {
            return 'natural_gas';
        }

        // No fuel type detected - don't silently assume diesel
        // Log warning for debugging and return null (will show "Generator" without fuel type)
        if (totalGeneratorSizeKw > 0) {
            console.warn('[SystemDesignSummary] Generator fuel_type not detected in inputs:', {
                generatorInput,
                chpInput,
                generatorSizeKw,
                chpSizeKw
            });
        }
        return null;
    };

    const generatorFuelType = getGeneratorFuelType();

    // Only show if at least one asset is present
    const hasAssets = (pvSizeKw && pvSizeKw > 0) ||
        (batterySizeKw && batterySizeKw > 0) ||
        (totalGeneratorSizeKw && totalGeneratorSizeKw > 0);

    if (!hasAssets) {
        return null;
    }

    const formatNumber = (value, decimals = 1) => {
        if (value === null || value === undefined) return '-';
        return value.toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    const formatFuelType = (fuelType) => {
        if (!fuelType) return null; // Don't display if fuel type unknown
        const fuelMap = {
            'diesel': 'Diesel',
            'natural_gas': 'Natural Gas',
            'propane': 'Propane',
            'biogas': 'Biogas',
            'diesel_and_natural_gas': 'Diesel & Natural Gas'
        };
        return fuelMap[fuelType] || fuelType.charAt(0).toUpperCase() + fuelType.slice(1).replace(/_/g, ' ');
    };

    return (
        <Card
            sx={{
                mb: 3,
                bgcolor: 'background.paper',
                border: `2px solid ${vortxColors.gold}`,
                borderRadius: 2
            }}
        >
            <CardContent>
                <Typography
                    variant="h6"
                    sx={{
                        color: 'text.primary',
                        mb: 2,
                        fontWeight: 600,
                        fontFamily: vortxTypography.heading
                    }}
                >
                    System Design Summary
                </Typography>

                <Grid container spacing={3}>
                    {/* System Configuration */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 2,
                                bgcolor: isDark ? 'rgba(178, 140, 80, 0.2)' : 'rgba(178, 140, 80, 0.1)',
                                borderRadius: 1,
                                border: `1px solid ${vortxColors.gold}`
                            }}
                        >
                            <Power sx={{ color: vortxColors.gold, fontSize: 32 }} />
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}
                                >
                                    Configuration
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ color: 'text.primary', fontWeight: 700 }}
                                >
                                    {isOffGrid ? 'Off-Grid' : 'Grid-Connected'}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Solar PV */}
                    {pvSizeKw > 0 && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 2,
                                    bgcolor: isDark ? 'rgba(178, 140, 80, 0.2)' : 'rgba(178, 140, 80, 0.1)',
                                    borderRadius: 1,
                                    border: `1px solid ${vortxColors.gold}`
                                }}
                            >
                                <SolarPower sx={{ color: vortxColors.gold, fontSize: 32 }} />
                                <Box>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}
                                    >
                                        Solar PV
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ color: 'text.primary', fontWeight: 700 }}
                                    >
                                        {formatNumber(pvSizeKw)} kW
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    )}

                    {/* Battery Storage */}
                    {batterySizeKw > 0 && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 2,
                                    bgcolor: isDark ? 'rgba(178, 140, 80, 0.2)' : 'rgba(178, 140, 80, 0.1)',
                                    borderRadius: 1,
                                    border: `1px solid ${vortxColors.gold}`
                                }}
                            >
                                <Battery80 sx={{ color: vortxColors.gold, fontSize: 32 }} />
                                <Box>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}
                                    >
                                        Battery Storage
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ color: 'text.primary', fontWeight: 700 }}
                                    >
                                        {formatNumber(batterySizeKw)} kW
                                    </Typography>
                                    {batterySizeKwh > 0 && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            {formatNumber(batterySizeKwh)} kWh
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    )}

                    {/* Generator */}
                    {totalGeneratorSizeKw > 0 && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 2,
                                    bgcolor: isDark ? 'rgba(178, 140, 80, 0.2)' : 'rgba(178, 140, 80, 0.1)',
                                    borderRadius: 1,
                                    border: `1px solid ${vortxColors.gold}`
                                }}
                            >
                                <ElectricBolt sx={{ color: vortxColors.gold, fontSize: 32 }} />
                                <Box>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}
                                    >
                                        Generator
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ color: 'text.primary', fontWeight: 700 }}
                                    >
                                        {formatNumber(totalGeneratorSizeKw)} kW
                                    </Typography>
                                    {formatFuelType(generatorFuelType) && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            {formatFuelType(generatorFuelType)}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
}
